import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Description as DescriptionIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { LTT_STORE } from '../../config/constants';

/**
 * Modal for selecting LTT (Lock-Tag-Try Permit) in JHA Step 2
 * Options: Take Photo, Select Existing LTT, Create New LTT
 */
export default function LttSelectionModal({
    open,
    onClose,
    onSelectLtt,
    onCreateNew,
    jhaData // JHA data for filtering and pre-filling
}) {
    const [lttList, setLttList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLtt, setSelectedLtt] = useState(null);

    // Load LTT forms from IndexedDB
    useEffect(() => {
        if (open) {
            loadLttForms();
            setSelectedLtt(null);
        }
    }, [open]);

    const loadLttForms = async () => {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(LTT_STORE)) {
                const tx = db.transaction(LTT_STORE, 'readonly');
                const store = tx.objectStore(LTT_STORE);
                const allForms = await store.getAll();
                setLttList(allForms || []);
            }
        } catch (e) {
            console.error('Error loading LTT forms:', e);
        } finally {
            setLoading(false);
        }
    };

    // Filter LTT forms by job/location and date (+7 days)
    const filteredLttList = useMemo(() => {
        if (!lttList.length) return [];

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Get JHA job code or location for matching
        const jhaJobCode = jhaData?.stepOne?.jobNumber || '';
        const jhaLocation = jhaData?.stepOne?.locationName || jhaData?.stepOne?.jobSiteAddress || '';

        return lttList
            .filter(ltt => {
                // Only show submitted/completed forms
                if (ltt.status !== 'submitted' && ltt.status !== 'completed') {
                    return false;
                }

                // Check date within last 7 days
                const lttDate = ltt.meta?.submittedAt || ltt.updatedAt || ltt.createdAt;
                if (lttDate) {
                    const lttDateTime = new Date(lttDate);
                    if (lttDateTime < sevenDaysAgo) {
                        return false;
                    }
                }

                // Match by job code or location
                const lttJobCode = ltt.header?.jobCode || '';
                const lttLocation = ltt.header?.location || '';

                // If JHA has job code, match by job code
                if (jhaJobCode && lttJobCode) {
                    if (lttJobCode === jhaJobCode) return true;
                }

                // If JHA has location, match by location (partial match)
                if (jhaLocation && lttLocation) {
                    const jhaLocLower = jhaLocation.toLowerCase();
                    const lttLocLower = lttLocation.toLowerCase();
                    if (lttLocLower.includes(jhaLocLower) || jhaLocLower.includes(lttLocLower)) {
                        return true;
                    }
                }

                // If no job/location in JHA, show all recent
                if (!jhaJobCode && !jhaLocation) {
                    return true;
                }

                return false;
            })
            .sort((a, b) => {
                const dateA = new Date(a.meta?.submittedAt || a.updatedAt || a.createdAt || 0);
                const dateB = new Date(b.meta?.submittedAt || b.updatedAt || b.createdAt || 0);
                return dateB - dateA; // Newest first
            });
    }, [lttList, jhaData]);

    // Check if selected LTT has all required signatures
    const validateLttSignatures = (ltt) => {
        const missing = [];
        const signatures = ltt?.signatures || {};

        if (!signatures.permitIssuer?.signature) missing.push('Permit Issuer');
        if (!signatures.permitAcceptor?.signature) missing.push('Permit Acceptor');
        if (!signatures.handBack?.signature) missing.push('Hand Back');
        if (!signatures.cancellation?.signature) missing.push('Cancellation');

        return { isValid: missing.length === 0, missing };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSelectLtt = (ltt) => {
        setSelectedLtt(ltt);
    };

    const handleConfirmSelect = () => {
        if (selectedLtt) {
            onSelectLtt(selectedLtt);
            onClose();
        }
    };

    const handleCreateNew = () => {
        onCreateNew();
        onClose();
    };

    const selectedValidation = selectedLtt ? validateLttSignatures(selectedLtt) : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { maxHeight: '85vh' } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Lock-Tag-Try Permit</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
                {/* Action Button */}
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5, mb: 2 }}
                >
                    Create New Lock-Tag-Try Permit
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Chip label="or select existing" size="small" />
                </Divider>

                {/* Existing LTT List */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Recent LTT Permits (last 7 days, matching job/location)
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : filteredLttList.length === 0 ? (
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: '#fafafa' }}>
                        <Typography variant="body2" color="text.secondary">
                            No matching LTT permits found
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Create a new one or take a photo
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={1} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {filteredLttList.map((ltt) => {
                            const isSelected = selectedLtt?.uuid === ltt.uuid;
                            const location = ltt.header?.location || ltt.header?.jobName || 'No location';
                            const jobCode = ltt.header?.jobCode || '';
                            const workCarriedOutBy = ltt.header?.workCarriedOutBy || '';
                            const validation = validateLttSignatures(ltt);

                            return (
                                <Card
                                    key={ltt.uuid}
                                    variant="outlined"
                                    onClick={() => handleSelectLtt(ltt)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderWidth: isSelected ? 2 : 1,
                                        backgroundColor: isSelected ? 'primary.50' : 'white',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            backgroundColor: isSelected ? 'primary.50' : 'action.hover'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {isSelected ? (
                                                <CheckCircleIcon color="primary" />
                                            ) : (
                                                <DescriptionIcon color="disabled" />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                    {ltt.header?.mode === 'job' ? `${jobCode} - ${ltt.header?.jobName || ''}` : location}
                                                </Typography>
                                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                                    {workCarriedOutBy && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            By: {workCarriedOutBy}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(ltt.meta?.submittedAt || ltt.updatedAt)}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    color="success"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                                {!validation.isValid && (
                                                    <Chip
                                                        icon={<WarningIcon sx={{ fontSize: 12 }} />}
                                                        label="Missing signatures"
                                                        size="small"
                                                        color="warning"
                                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}

                {/* Validation warning for selected LTT */}
                {selectedLtt && selectedValidation && !selectedValidation.isValid && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight={500}>
                            Missing signatures: {selectedValidation.missing.join(', ')}
                        </Typography>
                        <Typography variant="caption">
                            You can still link this permit, but you'll need to complete all signatures before final JHA submission.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmSelect}
                    disabled={!selectedLtt}
                >
                    Select LTT
                </Button>
            </DialogActions>
        </Dialog>
    );
}
